import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState, Status } from 'store/types';

import { RealBalanceService } from 'api/realBalance';
import { LOGOUT_ACTION } from 'store/constants';

type RealBalanceType = {
  status: Status;
  amount: number;
  enablePayingFromBonusWallet: boolean;
  withdrawableAmount: number;
};

type BalanceUpdateType = {
  balance: number;
  amountToWager: number;
  enablePayingFromBonusWallet?: boolean;
};

type RealBalanceActionsType = {
  read: () => void;
  updateRealBalance: (params: BalanceUpdateType) => void;
  enablePayingFromBonusWallet: () => void;
};

const initialState: RealBalanceType = {
  amount: 0,
  status: 'idle',
  withdrawableAmount: 0,
  enablePayingFromBonusWallet: false,
};

const readRealBalanceThunk = createAsyncThunk('real-balance/read', async () => RealBalanceService.getRealBalance());
const enablePayingFromWallet = createAsyncThunk('real-balance/update', async (enable: boolean) =>
  RealBalanceService.enablePayingFromWallet(enable)
);

const realBalanceSlice = createSlice({
  initialState,
  name: 'realBalance',
  reducers: {
    updateRealBalance: (state, action) => {
      state.amount = action.payload.balance;
      state.withdrawableAmount = action.payload.balance - action.payload.amountToWager;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(readRealBalanceThunk.pending.type, (state: RealBalanceType): void => {
        state.status = 'loading';
      })
      .addCase(
        readRealBalanceThunk.fulfilled.type,
        (state: RealBalanceType, action: PayloadAction<BalanceUpdateType>): void => {
          state.status = 'success';
          state.amount = action.payload.balance;
          state.enablePayingFromBonusWallet = action.payload.enablePayingFromBonusWallet;
          state.withdrawableAmount = action.payload.balance - action.payload.amountToWager;
        }
      )
      .addCase(readRealBalanceThunk.rejected.type, (state: RealBalanceType): void => {
        state.status = 'failed';
        state.amount = initialState.amount;
      })
      .addCase(enablePayingFromWallet.pending.type, (state: RealBalanceType): void => {
        state.status = 'loading';
      })
      .addCase(enablePayingFromWallet.fulfilled.type, (state: RealBalanceType): void => {
        state.status = 'success';
        state.enablePayingFromBonusWallet = !state.enablePayingFromBonusWallet;
      })
      .addCase(enablePayingFromWallet.rejected.type, (state: RealBalanceType): void => {
        state.status = 'failed';
      })
      .addCase(LOGOUT_ACTION, (): RealBalanceType => initialState),
});

const useRealBalance = (): [RealBalanceType, RealBalanceActionsType] => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state: RootState) => state.realBalance);

  const actions = {
    read: () => {
      dispatch(readRealBalanceThunk());
    },
    updateRealBalance: (params: BalanceUpdateType) => {
      dispatch(realBalanceSlice.actions.updateRealBalance(params));
    },
    enablePayingFromBonusWallet: () => {
      dispatch(enablePayingFromWallet(!state.enablePayingFromBonusWallet));
    },
  };

  return [state, actions];
};

export { realBalanceSlice };
export default useRealBalance;
